import React, { useState } from "react";

export default function ActivitySoundwave({ activity, maxPages, dailyGoal, streaks }) {
  const [hoveredDay, setHoveredDay] = useState(null);

  return (
    <div style={{
      background: "var(--white)",
      border: "1px solid var(--dust)",
      borderRadius: "16px",
      padding: "1.75rem",
      boxShadow: "var(--shadow)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}>
      <div>
        <h3 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "1.35rem",
          color: "var(--ink)",
          fontWeight: 600,
          margin: "0 0 0.5rem 0",
        }}>
          Reading Consistency & Activity
        </h3>
        <p style={{
          fontSize: "0.85rem",
          color: "var(--muted)",
          margin: "0 0 1.25rem 0",
        }}>
          A high-fidelity record of your daily reading volume. Hover over any bar to inspect daily page counts.
        </p>

        {/* Heatmap/Soundwave Chart Container */}
        <div style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          height: "200px",
          background: "var(--hero-bg)",
          border: "1px solid var(--dust)",
          borderRadius: "16px",
          padding: "2rem 1.25rem 0.6rem",
          position: "relative",
          gap: "2px",
          overflow: "visible",
          marginBottom: "1rem",
          boxShadow: "inset 0 4px 20px rgba(0,0,0,0.05)",
        }}>
          {/* Faint Center Title Watermark */}
          <div style={{
            position: "absolute",
            top: "42%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "var(--gold)",
            opacity: 0.05,
            fontSize: "2.3rem",
            fontWeight: 700,
            fontFamily: "'Cormorant Garamond', serif",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            pointerEvents: "none",
            userSelect: "none",
            textAlign: "center",
          }}>
            HABIT SPECTRUM
          </div>

          {/* Y-Axis Guidelines & Metric Labels */}
          <div style={{ position: "absolute", left: 0, right: 0, top: "20%", height: "1px", borderTop: "1px dashed var(--gold)", opacity: 0.15, pointerEvents: "none" }} />
          <span style={{ position: "absolute", left: "12px", top: "calc(20% - 6px)", color: "var(--gold)", opacity: 0.6, fontSize: "0.55rem", fontWeight: 700, fontFamily: "monospace", letterSpacing: "0.08em", pointerEvents: "none" }}>
            PEAK: {maxPages} P
          </span>

          <div style={{
            position: "absolute",
            left: 0, right: 0,
            bottom: `${Math.min(90, Math.max(10, (dailyGoal / maxPages) * 75))}%`,
            height: "1px",
            borderTop: "1.5px dashed var(--gold)",
            opacity: 0.3,
            pointerEvents: "none"
          }} />
          <span style={{
            position: "absolute",
            left: "12px",
            bottom: `${Math.min(90, Math.max(10, (dailyGoal / maxPages) * 75))}%`,
            transform: "translateY(-50%)",
            color: "var(--gold)",
            fontSize: "0.55rem",
            fontWeight: 700,
            fontFamily: "monospace",
            letterSpacing: "0.08em",
            pointerEvents: "none"
          }}>
            GOAL: {dailyGoal} P
          </span>

          <div style={{ position: "absolute", left: 0, right: 0, bottom: "20px", height: "1px", borderTop: "1px solid var(--dust)", opacity: 0.3, pointerEvents: "none" }} />
          <span style={{ position: "absolute", left: "12px", bottom: "14px", color: "var(--muted)", opacity: 0.5, fontSize: "0.55rem", fontWeight: 700, fontFamily: "monospace", letterSpacing: "0.08em", pointerEvents: "none" }}>
            0 PAGES
          </span>

          {/* Background SVG Trendline */}
          <svg
            style={{
              position: "absolute",
              bottom: "0.6rem",
              left: "1.25rem",
              width: "calc(100% - 2.5rem)",
              height: "calc(100% - 2.5rem)",
              pointerEvents: "none",
              opacity: 0.18,
            }}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="goldTrendGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--gold)" stopOpacity="0.8" />
                <stop offset="100%" stopColor="var(--gold)" stopOpacity="0.0" />
              </linearGradient>
            </defs>
            {activity.length > 1 && (
              <path
                d={`M 0 100 ${activity.map((day, i) => {
                  const x = (i / 29) * 100;
                  const y = maxPages > 0 ? 100 - (day.count / maxPages) * 75 : 100;
                  return `L ${x} ${y}`;
                }).join(" ")} L 100 100 Z`}
                fill="url(#goldTrendGrad)"
              />
            )}
            {activity.length > 1 && (
              <path
                d={activity.map((day, i) => {
                  const x = (i / 29) * 100;
                  const y = maxPages > 0 ? 100 - (day.count / maxPages) * 75 : 100;
                  return `${i === 0 ? "M" : "L"} ${x} ${y}`;
                }).join(" ")}
                fill="none"
                stroke="var(--gold)"
                strokeWidth="1.8"
              />
            )}
          </svg>

          {/* Columns */}
          {activity.map((day) => {
            const heightPercent = maxPages > 0 ? (day.count / maxPages) * 100 : 0;
            const isHovered = hoveredDay && hoveredDay.date === day.date;
            
            const barFill = day.level === 0
              ? "var(--level-0)"
              : day.level === 1
              ? "var(--level-1)"
              : day.level === 2
              ? "var(--level-2)"
              : "var(--level-3)";

            const shadowColor = day.level === 0
              ? "none"
              : day.level === 1
              ? "var(--level-shadow-1)"
              : day.level === 2
              ? "var(--level-shadow-2)"
              : "var(--level-shadow-3)";

            return (
              <div
                key={day.date}
                onMouseEnter={() => setHoveredDay(day)}
                onMouseLeave={() => setHoveredDay(null)}
                style={{
                  height: "100%",
                  width: "calc(100% / 30)",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  cursor: "pointer",
                  position: "relative",
                  zIndex: isHovered ? 12 : 3,
                }}
              >
                {/* Visible Animated Capsule Bar */}
                <div style={{
                  height: `calc(8px + ${heightPercent * 1.1}px)`,
                  width: "60%",
                  background: barFill,
                  borderRadius: "4px 4px 0 0",
                  boxShadow: isHovered ? "0 0 15px var(--gold), " + shadowColor : shadowColor,
                  transform: isHovered ? "scaleX(1.3) scaleY(1.08)" : "none",
                  transformOrigin: "bottom",
                  borderTop: day.level === 0 ? "1px solid var(--dust)" : "none",
                  transition: "all 0.2s cubic-bezier(0.2, 0.8, 0.2, 1)",
                }} />

                {/* Floating Mini Hover Tooltip */}
                {isHovered && (
                  <div style={{
                    position: "absolute",
                    bottom: "100%",
                    left: "50%",
                    transform: "translateX(-50%) translateY(-10px)",
                    background: "var(--nav-bg)",
                    border: "1.5px solid var(--gold)",
                    borderRadius: "6px",
                    padding: "4px 8px",
                    color: "var(--nav-text)",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    fontFamily: "'Outfit', sans-serif",
                    whiteSpace: "nowrap",
                    boxShadow: "var(--shadow-lg)",
                    pointerEvents: "none",
                    zIndex: 100,
                    animation: "scaleUp 0.18s cubic-bezier(0.34, 1.56, 0.64, 1) both",
                  }}>
                    <div style={{ color: "var(--gold)", fontSize: "0.55rem", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px" }}>
                      {day.formattedDate}
                    </div>
                    <div>{day.count} pages</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Heatmap Legend */}
      <div style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "8px",
        fontSize: "0.78rem",
        color: "var(--muted)",
        fontFamily: "'Outfit', sans-serif",
      }}>
        <span>Less</span>
        <div style={{ width: "12px", height: "12px", background: "var(--level-0)", border: "1px solid var(--dust)", borderRadius: "3px" }} />
        <div style={{ width: "12px", height: "12px", background: "var(--level-1)", borderRadius: "3px" }} />
        <div style={{ width: "12px", height: "12px", background: "var(--level-2)", borderRadius: "3px" }} />
        <div style={{ width: "12px", height: "12px", background: "var(--level-3)", borderRadius: "3px" }} />
        <span>More</span>
      </div>

      {/* Mini Habits Stats Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "0.75rem",
        marginTop: "1.25rem",
        paddingTop: "1.25rem",
        borderTop: "1px dashed var(--dust)",
      }}>
        <div style={{
          background: "var(--cream)",
          padding: "0.75rem",
          borderRadius: "10px",
          border: "1px solid var(--dust)",
        }}>
          <div style={{ fontSize: "0.68rem", color: "var(--muted)", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.05em", fontFamily: "'Outfit', sans-serif" }}>Consistency Score</div>
          <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--ink)", marginTop: "2px", display: "flex", alignItems: "center", gap: "4px", fontFamily: "'Outfit', sans-serif" }}>
            📈 {Math.round((activity.filter(d => d.count > 0).length / 30) * 100)}% 
            <span style={{ fontSize: "0.72rem", color: "var(--muted)", fontWeight: 400 }}>({activity.filter(d => d.count > 0).length}/30d)</span>
          </div>
        </div>
        <div style={{
          background: "var(--cream)",
          padding: "0.75rem",
          borderRadius: "10px",
          border: "1px solid var(--dust)",
        }}>
          <div style={{ fontSize: "0.68rem", color: "var(--muted)", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.05em", fontFamily: "'Outfit', sans-serif" }}>Streak Level</div>
          <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--gold)", marginTop: "2px", fontFamily: "'Outfit', sans-serif" }}>
            ⚡ {streaks.currentStreak >= 30 ? "Elite Reader" : streaks.currentStreak >= 14 ? "Devoted Reader" : streaks.currentStreak >= 7 ? "Active Reader" : "Novice Reader"}
          </div>
        </div>
      </div>
    </div>
  );
}
